/** Auth pages (mockups/auth/*.html, docs/plan/screens-auth-app-widget.md §0–§1.7). */
export const auth = {
  aside: {
    homeLabel: "{{product}} home",
    pill: "Built for Philippine organizations",
    headline: "Answers your customers and community can trust — from your own documents.",
    citeTitle: "Every answer cites its source",
    citeBody: "People see the exact page of the guide, policy or price list it came from.",
    channelsTitle: "One assistant, every channel",
    channelsBody: "Your website, a web app link, your mobile app, and Facebook Messenger.",
    privacyTitle: "Data Privacy Act–ready",
    privacyBody: "Consent capture, retention controls, and audit logs aligned with RA 10173 and NPC guidance.",
    sampleLabel: "Front Desk Assistant · Widget",
    live: "Live",
    sampleQuestion: "Magkano po ang reservation fee?",
    sampleAnswerLead: "The reservation fee is",
    sampleAnswerAmount: "₱5,000",
    sampleAnswerTail: "and is deducted from your first payment. You can pay via GCash, Maya or card.",
    sampleSource: "Fees & Payment Guide 2026.pdf · p. 2",
    footer: "© 2026 {{company}} Inc. · Clark Freeport Zone, Pampanga",
    hosting: "Hosted in Singapore",
  },
  common: {
    workEmail: "Work email",
    password: "Password",
    passwordPlaceholder: "Enter password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    or: "or",
    backToSignIn: "Back to sign in",
    back: "Back",
    signIn: "Sign in",
    alreadyHaveAccount: "Already have an account?",
    dismiss: "Dismiss",
    terms: "Terms",
    termsOfService: "Terms of Service",
    privacy: "Privacy",
    privacyNotice: "Privacy Notice",
    status: "Status",
    continueGoogle: "Continue with Google",
    continueMicrosoft: "Continue with Microsoft",
    google: "Google",
    microsoft: "Microsoft",
    notConfigured: "Not configured yet",
    securityCheck: "Security check",
    securityCheckHint: "Complete the security check to continue.",
    securityCheckFailed: "The security check couldn't load. Refresh the page and try again.",
    securityCheckRetry: "Try again",
    emailInvalid: "Enter a valid email address",
    emailRequired: "Enter your email address",
    emailTooLong: "That email address is too long",
    passwordRequired: "Enter your password",
    passwordTooLong: "That password is too long",
    nameRequired: "Enter your full name",
    loading: "Loading…",
    opensNewTab: "(opens in a new tab)",
  },
  password: {
    placeholder: "At least 10 characters",
    hintEmpty: "Use 10+ characters with a mix of letters, numbers & symbols",
    strength0: "Too weak",
    strength1: "Weak",
    strength2: "Fair",
    strength3: "Good",
    strength4: "Strong",
    strengthLabel: "Password strength: {{label}}",
    ruleLen: "10+ characters",
    ruleCase: "Upper & lowercase",
    ruleNum: "At least one number",
    ruleSym: "At least one symbol",
    ruleMatch: "Passwords match",
    rulesLabel: "Password requirements",
    ruleMet: "met",
    ruleNotMet: "not met",
    tooShort: "Use at least 10 characters",
    mismatch: "Passwords don't match",
  },
  otp: {
    digit: "Digit {{n}}",
    incomplete: "Enter all 6 digits",
  },
  login: {
    title: "Sign in · {{product}}",
    newHere: "New to {{product}}?",
    createAccount: "Create an account",
    heading: "Welcome back",
    subtitle: "Sign in to manage your assistant.",
    errorTitle: "That email and password don't match.",
    errorAttempts_one: "You have {{count}} attempt left before the account is locked for 15 minutes.",
    errorAttempts_other: "You have {{count}} attempts left before the account is locked for 15 minutes.",
    lockedTitle: "Too many failed sign-in attempts.",
    lockedBody: "This account is locked for 15 minutes. You can reset your password in the meantime.",
    resetLink: "Reset your password",
    forgot: "Forgot password?",
    remember: "Remember me on this device",
    submit: "Sign in",
    sso: "Sign in with SSO",
    ssoUnavailable: "SAML single sign-on is available on Pro and Institution plans. Ask your workspace Owner to set it up.",
    ssoTitle: "Sign in with SSO",
    ssoSub: "Use your organization’s single sign-on. We’ll find it from your email domain.",
    ssoContinue: "Continue with SSO",
    ssoNone: "Your company has not set up single sign-on for this address. Sign in with your password.",
    ssoEnforced: "Your workspace requires single sign-on. Continue with your company account.",
    ssoBack: "Back to sign in",
    ssoError: {
      failed: "Single sign-on did not work. Try again or ask your workspace owner.",
      unavailable: "Single sign-on is not available for that workspace.",
      not_a_member: "That account is not a member of the workspace. Ask an owner to invite you.",
      domain: "That e-mail domain is not set up for single sign-on.",
      expired: "The sign-in took too long. Start again.",
    },
    newOrg: "New organization?",
    staffTitle: "Staff of CRUD.IT?",
    staffBody: "Platform admin login",
    yourData: "Your data",
    oauthFailed: "Sign-in with that provider didn't finish. Try again or use your email and password.",
  },
  register: {
    title: "Create your workspace · {{product}}",
    trial: "14-day free trial · no credit card",
    heading: "Create your workspace",
    subtitle: "Set up an AI assistant that answers from your own documents in about 15 minutes.",
    aboutYou: "About you",
    fullName: "Full name",
    emailHint: "We'll send a 6-digit code to verify this address.",
    yourOrganization: "Your organization",
    orgName: "Organization name",
    orgNameRequired: "Enter your organization's name",
    workspaceUrl: "Your workspace URL:",
    slugChecking: "Checking availability…",
    slugTaken: "That URL is taken — we'll add a suffix for you.",
    slugAvailable: "Available",
    orgType: "Industry",
    size: "Size",
    sizeUnder200: "Fewer than 200 people",
    size200to500: "200–500 people",
    size501to1500: "501–1,500 people",
    size1501to5000: "1,501–5,000 people",
    sizeOver5000: "More than 5,000 people",
    country: "Country",
    consentTermsLead: "I agree to the",
    consentTermsAnd: "and",
    consentDpaLead: "I consent to the processing of my personal data under the",
    consentDpaAct: "Data Privacy Act of 2012",
    consentDpaTail: "(RA 10173) for account setup and support.",
    consentMarketing: "Send me product updates and webinars (optional).",
    consentRequired: "Accept this to continue",
    submit: "Create workspace",
    orSignUpWith: "or sign up with",
    trialHeading: "Start your 14-day free trial — no credit card",
    trialSub: "Set up an AI assistant that answers from your own documents in about 15 minutes.",
    planBadgeLead: "Trying the",
    planBadgeTail: "plan",
    stepOf: "Step {{n}} of {{total}}",
    close: "Close",
    emailHelp: "We’ll send a 6-digit code to verify this address.",
    emailBad: "Enter a valid work email.",
    continue: "Continue",
    back: "Back",
    aboutSub: "You’ll be the owner of this workspace.",
    namePlaceholder: "Juan dela Cruz",
    pwMoreOne: "1 more character to go",
    pwMoreMany: "{{count}} more characters to go",
    pwMix: "Add a mix of letters and numbers",
    pwWeak: "Weak password",
    pwOkay: "Okay password",
    pwGood: "Good password",
    pwStrong: "Strong password",
    orgSub: "We’ll start you from a template for your industry. You can change it anytime.",
    orgPlaceholder: "Kalayaan Heights Academy",
    consentsTitle: "Almost there",
    consentsSub: "A couple of agreements, then your workspace is ready.",
    required: "Required",
    captchaChecking: "Verifying…",
    captchaPassed: "Security check passed",
    captchaTag: "Turnstile",
    verifyTitle: "Check your email",
    verifySubLead: "Enter the 6-digit code we sent to",
    verifyAction: "Verify",
    resendLead: "Didn’t get it?",
    resendWait: "Resend code in {{seconds}}s",
    resendNow: "Resend code",
    changeEmail: "Change email",
    noCodeLead: "No code? If you already have an account,",
    noCodeSignIn: "sign in",
    noCodeOr: "or",
    noCodeReset: "reset your password",
    doneTitle: "Your workspace is ready",
    doneWelcome: "Welcome, {{name}}! Your workspace is ready",
    doneStepsLabel: "Next steps",
    doneStep1: "Upload a few documents",
    doneStep1Hint: "PDFs, sheets, slides, scans or your website",
    doneStep2: "Try it in the Playground",
    doneStep2Hint: "Ask in English, Filipino or Taglish",
    doneStep3: "Install the widget",
    doneStep3Hint: "Widget Studio → Install, then paste the snippet",
    doneEta: "~5 min",
    openConsole: "Open the console",
    trialStarted: "14-day free trial started. No credit card on file.",
    trust: "Built for Philippine organizations · Data Privacy Act–ready · Hosted in Singapore · A CRUD.IT product",
    resumeNote: "We kept your answers, except the password. Enter it again to continue.",
  },
  verify: {
    title: "Verify your email · {{product}}",
    heading: "Check your inbox",
    sentLead: "We sent a 6-digit code to",
    sentTail: "It expires in 10 minutes.",
    yourEmail: "your email address",
    codeLabel: "Verification code",
    tip: "Tip: you can paste the whole code.",
    submit: "Verify email",
    didntGetIt: "Didn't get it?",
    resendIn: "Resend in {{seconds}}s",
    resend: "Resend code",
    resent: "New code sent to {{email}}",
    wrongAddress: "Wrong address?",
    changeEmail: "Change email",
    spamNote: "Check your Spam or Promotions folder. Email on managed company accounts may take up to 2 minutes.",
    missingEmail: "Open the link from your sign-up, or sign up again to get a new code.",
  },
  twoFactor: {
    title: "Two-factor verification · {{product}}",
    differentAccount: "Use a different account",
    heading: "Two-factor verification",
    appSubLead: "Open your authenticator app (Google Authenticator, Microsoft Authenticator, or Authy) and enter the 6-digit code for",
    backupSub: "Enter one of the 8-character backup codes you downloaded when you set up two-factor authentication.",
    notYou: "Not you?",
    codeLabel: "Authentication code",
    codeHint: "Codes refresh every 30 seconds.",
    backupLabel: "Backup code",
    backupHint: "Each of your 10 backup codes works once. You saved them when you turned on 2FA.",
    backupInvalid: "Enter the 8-character code, e.g. ABCD-1234",
    trust: "Trust this device for 30 days",
    submit: "Verify & sign in",
    useBackup: "Use a backup code instead",
    useApp: "Use your authenticator app instead",
    helpLead: "Lost your phone and backup codes? Ask another Owner in your workspace to reset 2FA, or",
    helpLink: "contact CRUD.IT Support",
    recoverySent: "Recovery request sent to CRUD.IT Support — expect an identity check within 1 business day",
    expired: "This sign-in attempt expired. Sign in again to get a new code.",
  },
  forgot: {
    title: "Forgot password · {{product}}",
    heading: "Reset your password",
    subtitle: "Enter your work email and we’ll send you a link to set a new one.",
    sentMessage: "If an account exists for that email, a reset link is on its way.",
    submit: "Send reset link",
    remembered: "Remembered it?",
    successHeading: "Check your inbox",
    successLead: "If an account exists for",
    successTail: "you'll get a reset link in a minute. The link expires in 30 minutes.",
    noteLead: "Didn't receive it? Check Spam, or",
    sendAgain: "send it again",
    noteTail: ".",
    team: "Team",
    sentAgain: "Reset link sent again",
    throttled: "Too many reset requests. Try again in about {{minutes}} min.",
  },
  reset: {
    title: "Set a new password · {{product}}",
    heading: "Set a new password",
    forLead: "For",
    forTail: "You'll be signed out of other devices.",
    newPassword: "New password",
    confirm: "Confirm new password",
    submit: "Update password",
    done: "Password updated. Sign in with your new password.",
    invalidTitle: "This reset link has expired",
    invalidBody: "Reset links work once and expire after 30 minutes. Request a new one to continue.",
    requestNew: "Request a new link",
  },
  invite: {
    title: "Accept invitation · {{product}}",
    signInToAccept: "Sign in to accept",
    invitedYou: "{{inviter}} invited you to join {{workspace}} on {{product}} as",
    invitedYouNoInviter: "You're invited to join {{workspace}} on {{product}} as",
    workspace: "Workspace",
    role: "Role",
    invitedBy: "Invited by",
    expires: "Expires",
    expiresIn_one: "In {{count}} day · {{date}}",
    expiresIn_other: "In {{count}} days · {{date}}",
    expiresToday: "Today · {{date}}",
    whatCan: "What can {{article}} {{role}} do?",
    cannot: "Cannot manage {{modules}}",
    articleA: "a",
    articleAn: "an",
    setupHeading: "Set up your account",
    joiningAs: "You're joining as",
    orSetPassword: "or set a password",
    jobTitle: "Job title",
    optional: "(optional)",
    consentLead: "I agree to the",
    consentTail: "and consent to processing of my data under the Data Privacy Act of 2012.",
    submit: "Accept invitation & join",
    notYou: "Not you, or not expecting this?",
    decline: "Decline invitation",
    declined: "Invitation declined — {{inviter}} has been notified",
    declinedNoInviter: "Invitation declined",
    signedInAs: "You're signed in as {{email}}.",
    acceptSignedIn: "Accept invitation",
    mismatchTitle: "This invitation is for a different email",
    mismatchBody: "You're signed in as {{current}}, but the invitation was sent to {{invited}}. Sign out and open the link again.",
    signOut: "Sign out",
    invalidTitle: "This invitation isn't available",
    invalidBody: "It may have expired, been revoked, or already been accepted. Ask the person who invited you to send a new one.",
    goToSignIn: "Go to sign in",
    loadFailedTitle: "We couldn't load your invitation",
    loadFailedBody: "The link may still be good. Check your connection and try again.",
    retry: "Try again",
    joined: "Welcome to {{workspace}}",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "We couldn't reach the server. Check your connection and try again.",
    rateLimited: "Too many attempts. Wait a moment and try again.",
    invalidCode: "That code is not correct.",
    invalidCodeAttempts_one: "That code is not correct. {{count}} attempt left.",
    invalidCodeAttempts_other: "That code is not correct. {{count}} attempts left.",
    codeExpired: "That code has expired. Request a new one.",
    tooManyAttempts: "Too many attempts. Request a new code.",
    weakPassword: "Use at least 10 characters for your password.",
    breachedPassword: "This password has appeared in a data breach. Choose a different password.",
    captchaRequired: "Please complete the security check to continue.",
    captchaFailed: "The security check failed. Please try again.",
    emailTaken: "An account with this email already exists. Sign in instead.",
  },
} as const;
